import React, { FC, forwardRef } from 'react'
import dynamic from 'next/dynamic'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useCookies } from 'react-cookie'
import { toggleCustomizeModal } from 'src/redux/nlt-offer/actions'
import brands from 'src/containers/data/brands.json'

const OfferDetails = dynamic(() => import('src/components/OfferDetails'))

interface Props {
  offerData: OfferGlobal.RentOfferDetail
  ref?: any
}

const OfferDetailsContainer: FC<Props> = forwardRef(({ offerData }, ref) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [, setCookie, removeCookie] = useCookies()
  const userProfile = useSelector((state: Store.RootState) => state.user?.profile)
  const priceVatIncluded = useSelector((state: Store.RootState) => state.nlt.priceVatIncluded)
  const openCustomizeModal = step => {
    if (step > 0) {
      setCookie('hideStep0', '1', { path: '/' })
    } else {
      removeCookie('hideStep0', { path: '/' })
    }

    dispatch(toggleCustomizeModal({ step }))
  }

  let modalStepToPreconfigured = 1;
  let modalStepToCustom = 0;

  if (userProfile && userProfile.id) { // utente riconosciuto, deve solo ri-confermare i propri dati
    modalStepToPreconfigured = 2
    modalStepToCustom = 2
  } else if (userProfile && userProfile.deal) { // configurazione offerta preesistente, vai al chatbot
    modalStepToPreconfigured = 3
    modalStepToCustom = 3
  }

  const title = `${offerData.vehicle.name}`
  const primaryButton = {
    title: t('offerContainer:primaryTitle', { value: title }),
    action: t('offerContainer:primaryAction'),
    onClick: () => openCustomizeModal(modalStepToPreconfigured),
  }
  const secondaryButton = {
    title: t('offerContainer:secondaryTitle', { value: title }),
    action: t('offerContainer:secondaryAction'),
    onClick: () => openCustomizeModal(modalStepToCustom),
  }

  let limitedOfferBadge = null

  if (offerData.expirationDate) {
    const offerTimeStamp = Date.parse(offerData.expirationDate)
    const nowTimeStamp = (new Date()).getTime()
    const diffTimeStamp = offerTimeStamp - nowTimeStamp

    if (diffTimeStamp > 0) {
      limitedOfferBadge = t('nltOffersComponent:limitedOfferBadge', {
        value: Math.floor((diffTimeStamp) / (24 * 60 * 60 * 1000))
      })
    }
  }

  const priceValue = priceVatIncluded ? Math.round(offerData.monthlyRate * 1.22) : offerData.monthlyRate
  const depositValue = offerData.deposit ?
    priceVatIncluded ? Math.round(offerData.deposit * 1.22) : offerData.deposit
    : 0
  const depositDescription = priceVatIncluded ? t('offerContainer:depositDescriptionVatIncluded') : t('offerContainer:depositDescription')

  const brandImage = (brands.find(brand => brand.href === offerData.vehicle.brandCode) || { image: null }).image
  const brandImageAlt = `Logo ${offerData.vehicle.brand} Noleggio a Lungo Termine`

  return (
    <OfferDetails
      title={title}
      subtitle={offerData.vehicle.version}
      brandImage={brandImage}
      brandImageAlt={brandImageAlt}
      sectionTitle={t('offerContainer:sectionTitle')}
      durationTitle={t('offerContainer:durationTitle')}
      durationValue={t('offerContainer:durationValue', { value: offerData.contractLength.toLocaleString('it-IT') })}
      distanceTitle={t('offerContainer:distanceTitle')}
      distanceValue={t('offerContainer:distanceValue', { value: offerData.annualMileage == 0 ? 'illimitati' : offerData.annualMileage.toLocaleString('it-IT') })}
      depositTitle={t('offerContainer:depositTitle')}
      depositValue={depositValue ? t('offerContainer:depositValue', { value: depositValue.toLocaleString('it-IT') }) : t('offerContainer:depositValueZero')}
      depositDescription={depositValue ? depositDescription : ''}
      fuelTitle={t('offerContainer:fuelTitle')}
      fuelValue={t('offerContainer:fuelValue', { value: offerData.vehicle.engine.fuel })}
      transmissionTitle={t('offerContainer:transmissionTitle')}
      transmissionValue={t('offerContainer:transmissionValue', { value: offerData.vehicle.engine.gear })}
      powerTitle={t('offerContainer:powerTitle')}
      powerValue={t('offerContainer:powerValue', { value: offerData.vehicle.engine.displacement })}
      priceValue={t('offerContainer:priceValue', { value: priceValue.toLocaleString('it-IT') })}
      priceDescription={t('offerContainer:priceDescription')}
      insurances={[t('offerContainer:insuranceOne'), t('offerContainer:insuranceTwo')]}
      onHoverMessage={t('offerContainer:onHoverMessage', { value: title })}
      onClick={() => openCustomizeModal(0)}
      expirationValue={limitedOfferBadge}
      hasSecondaryAction={offerData.variations.length > 0}
      secondary={secondaryButton}
      active={offerData.status}
      partnership={offerData?.partnership}
      primary={primaryButton}
      ref={ref}
    />
  )
})

export default OfferDetailsContainer
